import _get from 'lodash/get';
import { factory as splitFactory, store } from 'src/main';

const SPLIT_IO_TREATMENT = 'enterprise_v2_auth';
const AUTH_METHOD_KEY = 'sl-auth-method';

// Local testing override: set to true/false to bypass Split.io, or null to use Split.io
const LOCAL_OVERRIDE: boolean | null = null;

/**
 * Evaluate the Split.io treatment for a specific email.
 * Used on enterprise-sso after the user enters their email in the sign-in form.
 */
export function evaluateV2ForEmail(email: string): boolean {
  if (LOCAL_OVERRIDE !== null) return LOCAL_OVERRIDE;

  const splitClient = splitFactory.client();
  const urlParams = new URLSearchParams(window.location.search);

  return (
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      host: window.location.hostname,
      referrer: urlParams.get('referrer') || '',
      email,
    }) === 'on'
  );
}

export function setAuthMethod(method: 'v1' | 'v2'): void {
  try { localStorage.setItem(AUTH_METHOD_KEY, method); } catch { /* ignore */ }
}

export function clearAuthMethod(): void {
  try { localStorage.removeItem(AUTH_METHOD_KEY); } catch { /* ignore */ }
}

export function getAuthMethod(): string | null {
  try { return localStorage.getItem(AUTH_METHOD_KEY); } catch { return null; }
}

/**
 * Determine whether to use the V2 auth flow on app hosts.
 *
 * Not a React hook — pure helper that reads localStorage, Redux store, and
 * Split.io synchronously. Safe to call from handlers, callbacks, and
 * non-component code.
 *
 * Evaluation order:
 * 1. LOCAL_OVERRIDE (dev bypass)
 * 2. sl-auth-method from localStorage (authoritative for current session — prevents mid-session switching)
 * 3. Split.io with email from Redux (only effective before sl-auth-method is set, e.g. on enterprise-sso)
 * 4. Default: V1. Auth routes (/sign_in, /sign_up_invite, etc.) short-circuit
 *    to AuthShell in main.tsx, so this fallback only fires for direct
 *    navigation to an app path while signed-out — both shells then 401-redirect
 *    through AuthShell anyway, so V1 is the safer default.
 */
export function isV2Enabled(): boolean {
  if (LOCAL_OVERRIDE !== null) return LOCAL_OVERRIDE;

  const storedMethod = getAuthMethod();
  if (storedMethod === 'v2') return true;
  if (storedMethod === 'v1') return false;

  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  if (!userEmail) {
    return false;
  }

  const splitClient = splitFactory.client();
  const urlParams = new URLSearchParams(window.location.search);
  const divisionId = _get(store.getState(), ['user', 'session', 'divisionId'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], '');

  return (
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      host: window.location.hostname,
      referrer: urlParams.get('referrer') || '',
      email: userEmail,
      divisionId,
      beaconClientId
    }) === 'on'
  );
}
